import useSWR from 'swr'
import { useSession } from 'next-auth/react'
import { useRegistry } from '@/hooks/useRegistry'
import { fetch } from '@/hooks/index/useDocuments/lib/fetch'
import { memoQueryParams, validMemoDays } from '@/hooks/index/useDocuments/queries/views/memo'
import { toMemoItems } from '@/shared/templates/memos/lib/toMemoItems'
import type { MemoItem } from '@/shared/templates/memos/lib/renderMemo'
import { defaultBlockId, memoCandidateColumn } from '@/shared/templates/memos'
import type { MemoCandidateColumn, MemoSource, MemoTemplate } from '@/shared/templates/memos'
import { fields as printFlowFields } from '@/shared/schemas/printFlow'
import type { PrintFlow, PrintFlowFields } from '@/shared/schemas/printFlow'
import type { HitV1 } from '@ttab/elephant-api/index'
import type { Index } from '@/shared/Index'
import type { Repository } from '@/shared/Repository'
import type { Session } from 'next-auth'

/**
 * What the creation dialog narrows the candidate list by, one entry per
 * candidate column, each filterable independently of the others. `print` is a
 * column like the rest but never carries a value here: a print article has
 * neither a newsvalue nor a section to narrow it by, so that column is offered
 * no filters at all - see `hasFilters` in `CandidateColumn`.
 *
 * `from` and `to` are `YYYY-MM-DD` and inclusive, and `timeZone` is what they
 * are days in - the registry timezone, not the browser's. `ranges` overrides
 * them for a column with days of its own - see `hasColumnDates`.
 */
export interface MemoCandidateFilter extends MemoDayRange {
  timeZone: string
  ranges?: Partial<Record<MemoCandidateColumn, MemoDayRange>>
  newsvalues?: Record<MemoCandidateColumn, string[]>
  sections?: Record<MemoCandidateColumn, string[]>
  stories?: Record<MemoCandidateColumn, string>
}

/** An inclusive span of `YYYY-MM-DD` days. */
export interface MemoDayRange {
  from: string
  to: string
}

/** What every index query is made with, looked up once and shared by the columns. */
interface IndexClients {
  index: Index | undefined
  repository: Repository | undefined
  session: Session | null
}

/**
 * Fetch the candidate items for a memo, one index query per template source,
 * mapped to memo items and into the template's default block - which is where
 * a pick waits rather than where it ends up. Which block an item is rendered
 * under is the editor's answer on the batch bar, or the one block left where
 * there is only one, not something the fetch predetermines.
 *
 * Each candidate column is fetched and cached under a key of its own, so
 * changing one column's days or filters leaves the others' lists as they are.
 *
 * `useDocuments` is not used here because it requires a TableProvider ancestor
 * and sets table state, neither of which applies to the creation dialog.
 *
 * `enabled` is how the dialog holds a query back while what it would ask for is
 * not settled yet - an unfinished date range, or a template whose section the
 * editor has not picked. Nothing is fetched and nothing is cached until then,
 * and a range whose ends are not days is held back the same way rather than
 * asked about: `memoQueryParams` refuses to build a query out of one.
 */
export function useMemoCandidates(
  template: MemoTemplate | undefined,
  filter: MemoCandidateFilter,
  enabled = true
): {
  items: MemoItem[]
  isLoading: boolean
  error: Error | undefined
} {
  const { index, repository } = useRegistry()
  const { data: session } = useSession()
  const clients = { index, repository, session }
  const ready = enabled && !!session?.accessToken

  // One call per column rather than a loop, hooks being called unconditionally.
  const print = useColumnCandidates('print', template, filter, clients, ready)
  const planning = useColumnCandidates('planning', template, filter, clients, ready)
  const article = useColumnCandidates('article', template, filter, clients, ready)
  const events = useColumnCandidates('events', template, filter, clients, ready)

  const results = [print, planning, article, events]

  return {
    items: results.flatMap((result) => result.data ?? []),
    isLoading: results.some((result) => result.isLoading),
    error: results.find((result) => result.error)?.error
  }
}

/**
 * One candidate column's items, under a key holding only what that column is
 * asked about. No key, and so no fetch, for a column the template has no
 * source in.
 */
function useColumnCandidates(
  column: MemoCandidateColumn,
  template: MemoTemplate | undefined,
  filter: MemoCandidateFilter,
  clients: IndexClients,
  ready: boolean
): {
  data: MemoItem[] | undefined
  isLoading: boolean
  error: Error | undefined
} {
  const sources = template?.sources.filter(
    (source: MemoSource) => memoCandidateColumn(source.type) === column
  ) ?? []
  const days = filter.ranges?.[column] ?? filter

  const key = template && ready && sources.length && validMemoDays(days.from, days.to)
    ? [
        'memo-candidates',
        template.id,
        column,
        days.from,
        days.to,
        filter.timeZone,
        (filter.newsvalues?.[column] ?? []).join(','),
        (filter.sections?.[column] ?? []).join(','),
        filter.stories?.[column] ?? ''
      ]
    : null

  return useSWR<MemoItem[], Error>(key, async () => {
    if (!template) {
      return []
    }

    const flowUuids = sources.some((source) => source.flow)
      ? await printFlowUuids(clients)
      : new Map<string, string>()

    const perSource = await Promise.all(sources.map(async (source) => {
      const flowUuid = source.flow ? flowUuids.get(source.flow) : undefined

      // A source restricted to a print flow nobody has created contributes
      // nothing, rather than every print article there is.
      if (source.flow && !flowUuid) {
        console.warn(`No print flow named ${source.flow}`)

        return []
      }

      const hits = await fetch<HitV1, readonly string[]>({
        ...clients,
        ...memoQueryParams(source, {
          from: days.from,
          to: days.to,
          timeZone: filter.timeZone,
          newsvalues: filter.newsvalues?.[column],
          sections: filter.sections?.[column],
          story: filter.stories?.[column]
        }, flowUuid)
      })

      return toMemoItems(hits, source.type, defaultBlockId(template), filter.timeZone)
    }))

    return dedupe(perSource.flat())
  })
}

/**
 * The uuid of every print flow, by title.
 *
 * A template names the flow it draws print articles from by title, since that
 * is what an editor knows it as, but the article carries the flow as a link and
 * the title on that link is full text indexed - only its uuid can be matched
 * exactly. There are a handful of flows, so all of them are fetched and the
 * title is matched here rather than left to the index.
 */
async function printFlowUuids(
  { index, repository, session }: IndexClients
): Promise<Map<string, string>> {
  const hits = await fetch<PrintFlow, PrintFlowFields>({
    index,
    repository,
    session,
    documentType: 'tt/print-flow',
    fields: printFlowFields
  })

  return new Map(
    hits
      .map((hit): [string, string] => [hit.fields['document.title']?.values[0] ?? '', hit.id])
      .filter(([title]) => !!title)
  )
}

/**
 * Two hits that land in the same candidate column can still be the same thing
 * twice - the same planning returned by an overlapping date range, or a
 * recurring event's occurrence indexed more than once. Keep the first, which
 * follows the template's source order.
 *
 * Deduping is per column, not across the whole list: a story that is both a
 * planning and a published article is offered as both, in their own columns,
 * since the editor picks each independently - collapsing them into one row
 * would be assuming which of the two belongs in the memo.
 *
 * The day is part of the key because a recurring event is a separate document
 * per occurrence, all of them sharing a title and rarely carrying a slugline.
 * Over a range that would leave a weekly press conference in the memo once,
 * on the first week it falls in.
 */
function dedupe(items: MemoItem[]): MemoItem[] {
  const seen = new Set<string>()

  return items.filter((item) => {
    const column = memoCandidateColumn(item.sourceType)
    const key = `${column}|${item.headline}|${item.slugline ?? ''}|${item.startDate ?? ''}`

    if (seen.has(key)) {
      return false
    }

    seen.add(key)

    return true
  })
}
